/**
 * Validação da etapa 1 da adesão (at-58, C1).
 *
 * Existe porque, até aqui, a **única** validação da tela era "campo não vazio"
 * (mais o `type="email"` do navegador): CPF inválido, e-mail malformado e data
 * impossível passavam, e o consultor só descobria o erro **na etapa 3**, depois
 * de preencher o veículo e fotografar os documentos (spec 03 §6). Tudo aqui é
 * verificável **no cliente**, sem servidor.
 *
 * 🔴 **Funções puras, sem estado e sem rede.** A checagem de CPF já cadastrado
 * (decisão D3), que precisa da API, NÃO é daqui — mora na tela, porque tem regra
 * de fluxo (não bloqueia, falha em silêncio).
 */

const MENSAGEM_OBRIGATORIO = 'Obrigatório';

/** Idade máxima plausível — barra ano digitado errado ("1899", "0202"). */
const IDADE_MAXIMA = 120;

/**
 * Dígitos verificadores de CPF. Rejeita também os 11 repetidos
 * ("111.111.111-11"), que passam no cálculo mas não existem.
 */
export function cpfValido(valor: string): boolean {
  const d = (valor || '').replace(/\D/g, '');
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;

  const digito = (ate: number): number => {
    let soma = 0;
    for (let i = 0; i < ate; i += 1) soma += Number(d[i]) * (ate + 1 - i);
    const resto = (soma * 10) % 11;

    return resto === 10 ? 0 : resto;
  };

  return digito(9) === Number(d[9]) && digito(10) === Number(d[10]);
}

/**
 * Dígitos verificadores de CNPJ, 14 dígitos.
 *
 * ⚠️ Os pesos correm **da direita para a esquerda**, começando em 2 e voltando a
 * 2 depois do 9 — o que, para os 12 primeiros dígitos, dá a sequência conhecida
 * 5,4,3,2,9,8,7,6,5,4,3,2. Começar o peso por outro valor produz um validador que
 * recusa CNPJ legítimo (foi o que o teste pegou aqui: `11.222.333/0001-81`, que é
 * válido, era rejeitado).
 */
export function cnpjValido(valor: string): boolean {
  const d = (valor || '').replace(/\D/g, '');
  if (d.length !== 14 || /^(\d)\1{13}$/.test(d)) return false;

  const digito = (ate: number): number => {
    let soma = 0;
    let peso = 2;
    for (let i = ate - 1; i >= 0; i -= 1) {
      soma += Number(d[i]) * peso;
      peso = peso === 9 ? 2 : peso + 1;
    }
    const resto = soma % 11;

    return resto < 2 ? 0 : 11 - resto;
  };

  return digito(12) === Number(d[12]) && digito(13) === Number(d[13]);
}

/** `null` quando está válido; a mensagem do campo quando não. */
export function validarCpfCnpj(valor: string): string | null {
  const d = (valor || '').replace(/\D/g, '');
  if (!d) return MENSAGEM_OBRIGATORIO;

  if (d.length === 11) return cpfValido(d) ? null : 'CPF inválido — confira os números';
  if (d.length === 14) return cnpjValido(d) ? null : 'CNPJ inválido — confira os números';

  return 'Informe 11 dígitos (CPF) ou 14 (CNPJ)';
}

/**
 * E-mail. Deliberadamente **permissivo**: só exige `algo@algo.algo` sem espaços.
 *
 * ⚠️ Não vale ser mais rígido que o backend — quem decide é o `@IsEmail` do
 * `CreateAssociadoDto`. Um regex esperto demais aqui recusaria endereço que o
 * cadastro aceitaria, e o consultor ficaria travado sem ter o que corrigir.
 */
export function validarEmail(valor: string): string | null {
  const v = (valor || '').trim();
  if (!v) return MENSAGEM_OBRIGATORIO;

  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v) ? null : 'E-mail inválido';
}

/**
 * Data de nascimento em ISO (`yyyy-mm-dd`, o valor do `<input type="date">`).
 *
 * ⚠️ Recusa **data futura** e ano implausível, e nada além disso — é o default da
 * pergunta **A1** do plano, que ficou sem resposta: *não se sabe se o SGA aceita
 * menor de idade como titular*, e inventar uma idade mínima barraria cadastro
 * legítimo. Se a associação definir o mínimo, ele entra aqui.
 *
 * ⚠️ A comparação é feita em UTC, com a data montada por componente. `new
 * Date('2000-01-15')` é interpretado como UTC e, num fuso negativo como o do
 * Brasil, vira 14/01 local — uma data de hoje seria lida como ontem/amanhã na
 * virada do dia.
 */
export function validarDataNascimento(valor: string): string | null {
  const v = (valor || '').trim();
  if (!v) return MENSAGEM_OBRIGATORIO;

  const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!partes) return 'Data inválida';

  const [, ano, mes, dia] = partes.map(Number) as unknown as [string, number, number, number];
  const data = new Date(Date.UTC(ano, mes - 1, dia));

  // Rejeita dia que não existe no mês (31/02 vira 03/03 na normalização do Date).
  if (
    data.getUTCFullYear() !== ano ||
    data.getUTCMonth() !== mes - 1 ||
    data.getUTCDate() !== dia
  ) {
    return 'Data inválida';
  }

  const hoje = new Date();
  const hojeUtc = Date.UTC(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());

  if (data.getTime() > hojeUtc) return 'A data não pode ser no futuro';
  if (ano < hoje.getFullYear() - IDADE_MAXIMA) return 'Confira o ano de nascimento';

  return null;
}

/**
 * Telefone com DDD no mesmo campo: 10 dígitos (fixo) ou 11 (celular).
 *
 * `obrigatorio: false` aceita vazio — é o caso do telefone fixo, que segue
 * opcional; preenchido pela metade continua sendo erro.
 */
export function validarTelefone(valor: string, obrigatorio: boolean): string | null {
  const d = (valor || '').replace(/\D/g, '');
  if (!d) return obrigatorio ? MENSAGEM_OBRIGATORIO : null;
  if (d.length < 10) return 'Informe DDD + número';
  // 3º dígito 9 é a marca do celular; o campo do fixo tem 10 dígitos.
  if (d.length > 11) return 'Número muito longo';

  return null;
}

export function validarCep(valor: string): string | null {
  const d = (valor || '').replace(/\D/g, '');
  if (!d) return MENSAGEM_OBRIGATORIO;

  return d.length === 8 ? null : 'CEP tem 8 dígitos';
}

/** Campo de texto simples: só exige conteúdo depois do `trim`. */
export function validarObrigatorio(valor: string): string | null {
  return (valor || '').trim() ? null : MENSAGEM_OBRIGATORIO;
}

/**
 * Texto do CPF já cadastrado na etapa 1 (at-83) — o mesmo do 409 do backend.
 * Manda falar com o TI: o SAAC não acrescenta um 2º veículo a associado existente,
 * e a Substituição Veicular TROCA o veículo.
 */
export function mensagemCpfJaCadastrado(
  nome: string | null | undefined,
  consultor: string | null | undefined,
): string {
  const quem = nome?.trim() ? `${nome.trim()} já é associado(a)` : 'Este CPF/CNPJ já é de um associado';
  const carteira = consultor?.trim() ? `, na carteira de ${consultor.trim()}` : '';
  return `${quem}${carteira}. Para incluir outro veículo para esta pessoa, fale com o TI.`;
}
